import { Overlay } from '@angular/cdk/overlay';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY, MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { fuseAnimations } from '@fuse/animations/public-api';
import { SearchResultSet, AppSearchService } from 'app/core/services/app-search.service';
import { debounceTime, filter, map, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'search',
  templateUrl: './search.component.html',
  encapsulation: ViewEncapsulation.None,
  exportAs: 'fuseSearch',
  animations: fuseAnimations,
  imports: [
    MatButtonModule,
    MatIconModule,
    FormsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatOptionModule,
    RouterLink,
    NgTemplateOutlet,
    MatFormFieldModule,
    MatInputModule,
  ],
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      useFactory: () => {
        const overlay = inject(Overlay);
        return () => overlay.scrollStrategies.block();
      },
    },
  ],
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy {
  @Input() appearance: 'basic' | 'bar' = 'basic';
  @Input() debounce = 300;
  @Input() minLength = 2;

  @Output() search: EventEmitter<SearchResultSet[]> = new EventEmitter<SearchResultSet[]>();

  opened = false;
  resultSets: SearchResultSet[] | null = null;
  searchControl: UntypedFormControl = new UntypedFormControl('');

  private _matAutocomplete!: MatAutocomplete;
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private _elementRef: ElementRef,
    private _renderer2: Renderer2,
    private _appSearchService: AppSearchService,
  ) {}

  @HostBinding('class')
  get classList(): Record<string, boolean> {
    return {
      'search-appearance-bar': this.appearance === 'bar',
      'search-appearance-basic': this.appearance === 'basic',
      'search-opened': this.opened,
    };
  }

  @ViewChild('barSearchInput')
  set barSearchInput(value: ElementRef | undefined) {
    if (value) {
      setTimeout(() => {
        value.nativeElement.focus();
      });
    }
  }

  @ViewChild('matAutocomplete')
  set matAutocomplete(value: MatAutocomplete) {
    this._matAutocomplete = value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('appearance' in changes) {
      this.close();
    }
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(this.debounce),
        takeUntil(this._unsubscribeAll),
        map((value: string | null) => {
          const normalizedValue = typeof value === 'string' ? value : '';

          if (!normalizedValue || normalizedValue.length < this.minLength) {
            this.resultSets = null;
          }

          return normalizedValue;
        }),
        filter((value: string) => !!value && value.length >= this.minLength),
      )
      .subscribe((value: string) => {
        const resultSets = this._appSearchService.search(value);
        this.resultSets = resultSets;
        this.search.emit(resultSets);
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.code === 'Escape') {
      if (this.appearance === 'bar' && (!this._matAutocomplete || !this._matAutocomplete.isOpen)) {
        this.close();
      }
    }
  }

  open(): void {
    if (this.opened) {
      return;
    }

    this.opened = true;
  }

  close(): void {
    if (!this.opened) {
      return;
    }

    this.searchControl.setValue('');
    this.resultSets = null;
    this.opened = false;
  }

  trackByFn(index: number, item: any): any {
    return item?.id ?? index;
  }
}
